/*
 * Author: daiwei
 * File Created: Sunday, 24-02-04 09:16 pm
 * Last Modified: Monday, 25-04-28 05:32 pm, by Dw
 */

/* eslint-disable max-len */
/* eslint-disable no-console */
import dayjs from 'dayjs';

import { getEnv, isServer } from './env';
import { _typeof } from './object';

export enum LogLevel {
  debug, // 0
  trace, // 1
  info, // 2
  warn, // 3
  error, // 4
  silent, // 5
}

export type LogFormat = 'raw' | 'json' | 'string';

export type LogLevelKeys = keyof typeof LogLevel;

export interface CutsomLogger {
  debug(...msg: any[]): void;
  log(...msg: any[]): void;
  info(...msg: any[]): void;
  warn(...msg: any[]): void;
  error(...msg: any[]): void;
  [props: string]: any;
}

export interface LoggerOption {
  module?: string;
  prefix?: string;
  suffix?: string;
  format?: LogFormat;
  level?: LogLevel;
  fingerprint?: string | null;
}

export class Logger {
  private _logger: CutsomLogger | typeof console;

  private _logLevel: LogLevel;

  private _preLevel: LogLevel;

  private _module: string;

  private _format: LogFormat;

  private _prefix?: string;

  private _suffix?: string;

  private _fingerprint?: string | null = 'unknown';

  public constructor(options?: LoggerOption) {
    const env = getEnv();
    const {
      module = '',
      prefix,
      suffix,
      format,
      level,
      fingerprint,
    } = options || {};
    this._logger = console;
    this._logLevel = level === undefined ? LogLevel.trace : level;
    this._preLevel = this._logLevel;
    this._module = module;
    this._format = format || (isServer ? 'json' : 'raw');
    this._prefix = prefix;
    this._suffix = suffix;
    this._fingerprint = fingerprint;

    this._output = this._output.bind(this);
    this._handleMsg = this._handleMsg.bind(this);
    this.debug = this.debug.bind(this);
    this.log = this.log.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
    this.logger = this.logger.bind(this);
  }

  public clone(options?: LoggerOption) {
    const data = {
      module: options?.module || this._module,
      prefix: options?.prefix || this._prefix,
      suffix: options?.suffix || this._suffix,
      format: options?.format || this.format,
      level: options?.level || this.logLevel,
      fingerprint: options?.fingerprint || this._fingerprint,
    };
    return new Logger(data);
  }

  public get configs() {
    return {
      module: this._module,
      prefix: this._prefix,
      suffix: this._suffix,
      format: this._format,
      level: this._logLevel,
    };
  }

  public set configs(_config: {
    module?: string;
    prefix?: string;
    suffix?: string;
    format?: LogFormat;
    level?: LogLevel;
  }) {
    const { module, prefix, suffix, format, level } = _config;
    if (module) this._module = module;
    if (prefix) this._prefix = prefix;
    if (suffix) this._suffix = suffix;
    if (format) this._format = format;
    if (level) this.logLevel = level;
  }

  public get logLevel() {
    return this._logLevel;
  }

  public set logLevel(_level: LogLevel) {
    if (typeof _level !== 'number' || _level < 0 || _level > 6) {
      this.warn('[Logger Level]: Please pass 0 ~ 5 with number!');
      return;
    }
    this._preLevel = this._logLevel;
    this._logLevel = _level;
  }

  public set silent(_silent: boolean) {
    if (_silent && this._logLevel < LogLevel.silent) {
      this._preLevel = this._logLevel;
      this._logLevel = LogLevel.silent;
      return;
    }

    if (!_silent && this._logLevel === LogLevel.silent) {
      this._logLevel =
        this._preLevel < LogLevel.silent ? this._preLevel : LogLevel.info;
    }
  }

  public set module(_module: string) {
    this._module = _module;
  }

  public set prefix(_prefix: string) {
    this._prefix = _prefix;
  }

  public set suffix(_suffix: string) {
    this._suffix = _suffix;
  }

  public set format(_format: LogFormat) {
    this._format = _format;
  }

  public set customLogger(_logger: CutsomLogger) {
    this._logger = _logger;
  }

  private _getCallerInfo() {
    const error = new Error();
    const stack = error.stack?.split('\n') || [];
    // analyze the caller info
    const callerStack = stack[4] || ''; // 0: Error, 1: _getCallerInfo, 2: _output, 3: debug/log/info/warn/error, 4: caller
    const matchResult =
      callerStack.match(/at\s+(?:(.*)\s+\()?(.+):(\d+):(\d+)\)?/) || [];
    const methodName = matchResult[1] || 'unknown method';
    const fullPath = matchResult[2] || 'unknown file';
    const pathParts = fullPath.split(/[/\\]/);
    const lastTwoParts = pathParts.slice(-2).join('/');
    const fileName = lastTwoParts || 'unknown file';
    return { methodName, fileName };
  }

  private _output(params: { msg: any; level: keyof typeof LogLevel }): void {
    const { msg, level } = params;
    if (level === 'silent') return;
    const date = dayjs();
    const _logger = this._logger[level].bind(this._logger);
    const { methodName, fileName } = this._getCallerInfo();
    const commitHash = process.env.NEXT_PUBLIC_COMMIT_HASH || 'unknown commit';
    const completeMsg =
      this._prefix && this._suffix
        ? `<${this._prefix}> | ${msg} | <${this._suffix}>`
        : this._prefix
          ? `<${this._prefix}> | ${msg}`
          : this._suffix
            ? `${msg} | <${this._suffix}>`
            : msg;
    const fingerprint = this._fingerprint;
    switch (this._format) {
      case 'raw':
        _logger(msg);
        break;
      // used for server-side log by default
      case 'string':
        _logger(
          `[${level?.toUpperCase()}] [${date.format('YYYY-MM-DD HH:mm:ss.SSS')}] [${fingerprint}] [${commitHash}] [${this._module}] [${fileName}] [${methodName}] - ${completeMsg}`
        );
        break;
      case 'json':
      default: {
        const json = JSON.stringify({
          appName: process.env.APP_NAME ?? 'dicard-ui',
          fingerprint: fingerprint, // 假设 clientIp 是一个变量
          timestamp: date.valueOf(), // date 是 dayjs 实例 秒级别
          commit: commitHash, // commitHash 是一个变量
          module: this._module, // 假设 this._module 是一个类属性
          level: level?.toUpperCase(), // level 是一个变量
          fileName: fileName, // fileName 是一个变量
          message: completeMsg, // completeMsg 是一个变量
        });
        _logger(json);
      }
    }
  }

  private _handleMsg(msg: any[]): string {
    return msg
      .map((v) => {
        const _type = _typeof(v);
        if (_type === 'object' || _type === 'array') {
          try {
            return JSON.stringify(v);
          } catch (e) {
            try {
              return v?.toString?.();
            } catch (_e) {
              return v;
            }
          }
        }
        return v;
      })
      .join(', ');
  }

  public debug(...msg: any[]) {
    if (this._logLevel > LogLevel.debug) return;
    this._output({
      msg: this._handleMsg(msg),
      level: 'debug',
    });
  }

  public log(...msg: any[]) {
    if (this._logLevel > LogLevel.info) return;
    this._output({
      msg: this._handleMsg(msg),
      level: 'info',
    });
  }

  public info(...msg: any[]) {
    if (this._logLevel > LogLevel.info) return;
    this._output({
      msg: this._handleMsg(msg),
      level: 'info',
    });
  }

  public warn(...msg: any[]) {
    if (this._logLevel > LogLevel.warn) return;
    this._output({
      msg: this._handleMsg(msg),
      level: 'warn',
    });
  }

  public error(...msg: any[]) {
    if (this._logLevel > LogLevel.error) return;
    this._output({
      msg: this._handleMsg(msg),
      level: 'error',
    });
  }

  public logger(level: keyof typeof LogLevel = 'info', ...msg: any[]) {
    if (this._logLevel > LogLevel[level]) return;

    this._output({
      msg: this._handleMsg(msg),
      level,
    });
  }
}
